import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Input,
  InputNumber,
  Progress,
  Select,
  Space,
  Tag,
  TimePicker,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MedicineBoxOutlined,
  OrderedListOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  SYNC_SPECIES_LABELS,
} from '../constants/syncProtocolPresets';
import { fetchSyncProtocolTemplates } from '../services/syncProtocolTemplateService';
import api from '../services/api';
import {
  countCompletedSteps,
  createEmptySyncProtocolState,
  formatSyncStepActionLine,
  getSyncPresetsForSpeciesWithCustom,
  mapApiTemplateToPreset,
  parseDosageMl,
  parseSyncProtocolData,
  rebuildSyncStateFromPresetChange,
  resolveSyncPreset,
  updateSyncStep,
} from '../utils/syncProtocolUtils';
import SyncProtocolWrittenGuide from './SyncProtocolWrittenGuide';
import useMobile from '../hooks/useMobile';
import '../styles/sync-protocol.css';

const { Text } = Typography;
const { TextArea } = Input;

const ACTION_LABELS = {
  inject: 'Injection',
  insert: 'Insert device',
  remove: 'Remove device',
  observe: 'Observation',
  insemination: 'Insemination',
};

const ACTION_COLORS = {
  inject: 'blue',
  insert: 'purple',
  remove: 'orange',
  observe: 'default',
  insemination: 'green',
};

const HOW_IT_WORKS = [
  {
    key: 'pick',
    icon: <OrderedListOutlined />,
    title: 'Choose protocol',
    text: 'Select a preset matched to this animal’s species.',
  },
  {
    key: 'start',
    icon: <CalendarOutlined />,
    title: 'Set start (Step 1)',
    text: 'Only the first step date and time can be changed.',
  },
  {
    key: 'follow',
    icon: <CheckCircleOutlined />,
    title: 'Follow & complete',
    text: 'Other steps auto-calculate. Mark Done as you go.',
  },
];

function SyncProtocolScheduler({
  value,
  onChange,
  speciesKey,
  farmId,
  disabled,
  scheduleOnly = false,
  isEditing = false,
  disablePreset = false,
  disableProgramName = false,
}) {
  const navigate = useNavigate();
  const isMobile = useMobile(768);
  const controlSize = isMobile ? 'middle' : 'large';
  const isPresetLocked = Boolean(disabled || isEditing || disablePreset);
  const isProgramNameLocked = Boolean(disabled || isEditing || disableProgramName);
  const state = useMemo(() => parseSyncProtocolData(value), [value]);

  const { data: customTemplates = [] } = useQuery({
    queryKey: ['sync-protocol-templates'],
    queryFn: () => fetchSyncProtocolTemplates(),
    staleTime: 60 * 1000,
  });

  const { data: inventoryItems = [] } = useQuery({
    queryKey: ['inventory-list-sync', farmId, 'medicine'],
    queryFn: async () => {
      const res = await api.get('/inventory', {
        params: { farm_id: farmId, paginate: false, category: 'medicine' },
      });
      return res.data.data || [];
    },
    enabled: Boolean(farmId),
    staleTime: 60 * 1000,
  });

  const inventoryOptions = useMemo(
    () => inventoryItems.map((item) => {
      const stock = Number(item.current_stock ?? 0);
      const unit = item.unit || 'units';
      const isLow = Boolean(item.is_low_stock || (item.min_stock != null && stock <= Number(item.min_stock)));
      const stockText = stock > 0 ? `${stock.toLocaleString()} ${unit} left` : 'Out of stock';
      return {
        value: item.id,
        label: `${item.name} (${stockText})`,
        name: item.name,
        stock,
        unit,
        isLow,
      };
    }),
    [inventoryItems]
  );

  const customPresets = useMemo(
    () => customTemplates.map(mapApiTemplateToPreset).filter(Boolean),
    [customTemplates]
  );

  const presets = useMemo(
    () => getSyncPresetsForSpeciesWithCustom(speciesKey, customPresets),
    [speciesKey, customPresets]
  );

  const builtInPresets = useMemo(
    () => presets.filter((preset) => !preset.isCustom),
    [presets]
  );

  const myPresets = useMemo(
    () => presets.filter((preset) => preset.isCustom),
    [presets]
  );

  const selectedPreset = resolveSyncPreset(state.presetId, customPresets);
  const hasActiveProtocol = Boolean(state.presetId && state.steps?.length);
  const displayPreset = selectedPreset || (hasActiveProtocol ? {
    name: state.presetName || 'Synchronization protocol',
    summary: state.presetId?.startsWith('custom-') ? 'Custom saved template' : '',
    sourceNote: '',
  } : null);
  const completed = countCompletedSteps(state);
  const totalSteps = state.steps.length;
  const progressPct = totalSteps ? Math.round((completed / totalSteps) * 100) : 0;

  const emit = (next) => {
    onChange?.(next);
  };

  const handleProgramNameChange = (event) => {
    emit({
      ...state,
      programName: event.target.value,
    });
  };
  const handlePresetChange = (presetId) => {
    if (!presetId) {
      emit(createEmptySyncProtocolState());
      return;
    }
    emit(rebuildSyncStateFromPresetChange(
      presetId,
      dayjs().format('YYYY-MM-DD'),
      state,
      customPresets
    ));
  };

  const patchStep = (stepKey, patch) => {
    emit(updateSyncStep(state, stepKey, patch, customPresets));
  };

  const handleFirstStepDateChange = (date) => {
    if (!date || !state.steps.length) return;
    const first = state.steps[0];
    patchStep(first.key, { scheduledDate: date.format('YYYY-MM-DD') });
  };

  const handleFirstStepTimeChange = (time) => {
    if (!state.steps.length) return;
    const first = state.steps[0];
    patchStep(first.key, {
      scheduledTime: time ? time.format('HH:mm') : first.scheduledTime,
    });
  };

  const speciesLabel = speciesKey ? SYNC_SPECIES_LABELS[speciesKey] || speciesKey : null;

  return (
    <div
      className={`sync-protocol-scheduler${completed === totalSteps && totalSteps > 0 ? ' is-all-complete' : ''}${scheduleOnly ? ' is-schedule-only' : ''}${isMobile ? ' sync-protocol-scheduler--mobile' : ''}`}
    >
      <div className="sync-protocol-hero">
        <div className="sync-protocol-hero-text">
          <ScheduleOutlined className="sync-protocol-hero-icon" />
          <div>
            <Text strong className="sync-protocol-hero-title">Synchronization schedule</Text>
            <Text type="secondary" className="sync-protocol-hero-sub">
              {scheduleOnly
                ? 'Choose a protocol and set the first step start date. Mark steps done later from the sync calendar.'
                : speciesLabel
                  ? `Presets for ${speciesLabel}. Hormone steps and timed AI dates in one place.`
                  : 'Choose a protocol to build a step-by-step hormone and AI schedule.'}
            </Text>
          </div>
        </div>
      </div>

      {!scheduleOnly && !isMobile ? (
        <div className="sync-how-it-works">
          {HOW_IT_WORKS.map((item, index) => (
            <div key={item.key} className="sync-how-step">
              <span className="sync-how-num">{index + 1}</span>
              <span className="sync-how-icon">{item.icon}</span>
              <Text strong className="sync-how-title">{item.title}</Text>
              <Text type="secondary" className="sync-how-text">{item.text}</Text>
            </div>
          ))}
        </div>
      ) : null}

      <div className="sync-protocol-toolbar sync-protocol-toolbar--friendly">
        <div className="sync-protocol-field">
          <Text type="secondary" className="sync-protocol-label">Protocol preset</Text>
          <Select
            size={controlSize}
            placeholder={
              presets.length
                ? 'Select synchronization protocol…'
                : 'No presets for this species yet'
            }
            value={state.presetId}
            onChange={handlePresetChange}
            disabled={isPresetLocked || !presets.length}
            options={[
              ...(builtInPresets.length ? [{
                label: 'Built-in protocols',
                options: builtInPresets.map((p) => ({
                  value: p.id,
                  label: p.name,
                  summary: p.summary,
                  stepCount: p.stepCount,
                })),
              }] : []),
              ...(myPresets.length ? [{
                label: 'My saved templates',
                options: myPresets.map((p) => ({
                  value: p.id,
                  label: p.name,
                  summary: p.summary,
                  stepCount: p.stepCount,
                })),
              }] : []),
            ]}
            optionRender={(option) => (
              <div className="sync-preset-option">
                <Text strong>{option.label}</Text>
                <Text type="secondary" className="sync-preset-option-sub">
                  {option.data.summary} · {option.data.stepCount} steps
                </Text>
              </div>
            )}
            allowClear={!isPresetLocked}
            style={{ width: '100%' }}
          />
          {isPresetLocked && !disabled ? (
            <Text type="secondary" className="sync-protocol-field-hint" style={{ color: '#8c8c8c' }}>
              Protocol cannot be changed for an enrolled program.
            </Text>
          ) : null}
        </div>
        <div className="sync-protocol-field" style={{ marginTop: 10 }}>
          <Text type="secondary" className="sync-protocol-label">Program name</Text>
          <Input
            size={controlSize}
            placeholder="e.g. Autumn 2026 — Shed A"
            value={state.programName || ''}
            onChange={handleProgramNameChange}
            disabled={isProgramNameLocked || !state.presetId}
            maxLength={255}
          />
          {isProgramNameLocked && !disabled ? (
            <Text type="secondary" className="sync-protocol-field-hint" style={{ color: '#8c8c8c' }}>
              Program name is locked for this synchronization run.
            </Text>
          ) : (
            <Text type="secondary" className="sync-protocol-field-hint">
              This name is shown on animals so you can tell which sync run they belong to.
            </Text>
          )}
        </div>
        {!disabled && !isPresetLocked ? (
          <Button type="link" onClick={() => navigate('/synchronization/templates')} style={{ paddingLeft: 0 }}>
            Create / manage my templates
          </Button>
        ) : null}
      </div>

      {!state.presetId && presets.length > 0 && !disabled ? (
        <div className="sync-protocol-empty">
          <OrderedListOutlined className="sync-protocol-empty-icon" />
          <Text strong>Select a protocol above to generate your schedule</Text>
          <Text type="secondary">
            Each preset includes hormone injections, device insert/remove, and timed AI steps with field instructions.
          </Text>
        </div>
      ) : null}

      {displayPreset && (
        <>
          {scheduleOnly ? (
            <Alert
              type="info"
              showIcon
              className="sync-schedule-only-note"
              message="Set your start date and time. If step 1 (or any step) was already given in the yard, tick Done to record it."
            />
          ) : null}

          <div className="sync-protocol-meta sync-protocol-meta--friendly">
            <div>
              <Text strong className="sync-protocol-meta-title">{displayPreset.name}</Text>
              <Text type="secondary" className="sync-protocol-meta-sub">{displayPreset.summary}</Text>
            </div>
            {totalSteps > 0 && (
              <Tag color={completed === totalSteps ? 'success' : completed > 0 ? 'warning' : 'processing'} className="sync-progress-tag">
                {completed}/{totalSteps} completed
              </Tag>
            )}
          </div>

          {totalSteps > 0 ? (
            <div className="sync-progress-bar">
              <Progress
                percent={progressPct}
                size="small"
                strokeColor={completed === totalSteps ? '#52c41a' : completed > 0 ? '#faad14' : '#1677ff'}
                format={() => `${completed} of ${totalSteps}`}
              />
            </div>
          ) : null}

          <div className="sync-protocol-anchor-note">
            <CalendarOutlined />
            <Text type="secondary">
              <Text strong>Step 1 is your anchor.</Text>
              {' '}
              Change its date or time and every later step recalculates automatically.
            </Text>
          </div>

          <div className="sync-step-timeline">
            {state.steps.map((step, index) => {
              const isFirst = index === 0;
              const isLast = index === state.steps.length - 1;
              const isDone = step.status === 'done';
              const isAi = step.action === 'insemination';

              return (
                <div
                  key={step.key}
                  className={`sync-timeline-item${isDone ? ' is-done' : ''}${isFirst ? ' is-first' : ''}`}
                >
                  <div className="sync-timeline-rail">
                    <span className={`sync-timeline-dot${isDone ? ' is-done' : ''}${isAi ? ' is-ai' : ''}`}>
                      {isDone ? <CheckCircleOutlined /> : index + 1}
                    </span>
                    {!isLast && <span className="sync-timeline-line" aria-hidden />}
                  </div>

                  <div
                    className={`sync-step-card${isDone ? ' is-done' : ''}${isFirst ? ' is-first' : ''}${isAi ? ' is-ai' : ''}`}
                  >
                    <div className="sync-step-card-head">
                      <div className="sync-step-card-head-text">
                        <div className="sync-step-card-title-row">
                          <Text strong>{step.title}</Text>
                          <Tag color={ACTION_COLORS[step.action] || 'default'}>
                            {ACTION_LABELS[step.action] || step.action}
                          </Tag>
                          <Tag>Day +{step.dayOffset}</Tag>
                          {isFirst && <Tag color="green">Editable start</Tag>}
                        </div>
                        {step.hormone && (
                          <Text type="secondary" className="sync-step-card-hormone">
                            <MedicineBoxOutlined /> {step.hormone}
                          </Text>
                        )}
                      </div>
                      {!scheduleOnly && step.status === 'cancelled' && (
                        <Tag>Cancelled</Tag>
                      )}
                      {!scheduleOnly && step.status !== 'cancelled' && (
                        <Checkbox
                          checked={isDone}
                          disabled={disabled}
                          onChange={(e) => patchStep(step.key, {
                            status: e.target.checked ? 'done' : 'pending',
                            // Done when ticked, not when it was scheduled, so late steps show as late
                            actualDate: e.target.checked ? (step.actualDate || dayjs().format('YYYY-MM-DD')) : null,
                            actualTime: e.target.checked ? (step.actualTime || dayjs().format('HH:mm')) : null,
                          })}
                        >
                          Done
                        </Checkbox>
                      )}
                    </div>

                    <div className="sync-step-card-when">
                      {isFirst ? (
                        <>
                          <div className="sync-step-field">
                            <Text type="secondary" className="sync-step-field-label">Start date</Text>
                            <DatePicker
                              size={controlSize}
                              style={{ width: '100%' }}
                              value={step.scheduledDate ? dayjs(step.scheduledDate) : null}
                              onChange={handleFirstStepDateChange}
                              disabled={disabled}
                            />
                          </div>
                          <div className="sync-step-field">
                            <Text type="secondary" className="sync-step-field-label">Start time</Text>
                            <TimePicker
                              size={controlSize}
                              format="HH:mm"
                              style={{ width: '100%' }}
                              value={step.scheduledTime ? dayjs(step.scheduledTime, 'HH:mm') : null}
                              onChange={handleFirstStepTimeChange}
                              disabled={disabled}
                              allowClear={false}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="sync-step-calculated">
                          <ClockCircleOutlined />
                          <div>
                            <Text strong>
                              {step.scheduledDate ? dayjs(step.scheduledDate).format('dddd, DD MMM YYYY') : '—'}
                            </Text>
                            <Text type="secondary" className="sync-step-calculated-time">
                              at {step.scheduledTime || '08:00'} · auto-calculated
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* When it was actually given: ticking fills in now; change it when recording past work */}
                    {isDone && !scheduleOnly && (
                      <div className="sync-step-card-when">
                        <div className="sync-step-field">
                          <Text type="secondary" className="sync-step-field-label">Given on</Text>
                          <DatePicker
                            size={controlSize}
                            style={{ width: '100%' }}
                            value={step.actualDate ? dayjs(step.actualDate) : null}
                            disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
                            onChange={(d) => d && patchStep(step.key, { actualDate: d.format('YYYY-MM-DD') })}
                            disabled={disabled}
                            allowClear={false}
                          />
                        </div>
                        <div className="sync-step-field">
                          <Text type="secondary" className="sync-step-field-label">Time given</Text>
                          <TimePicker
                            size={controlSize}
                            format="HH:mm"
                            style={{ width: '100%' }}
                            value={step.actualTime ? dayjs(step.actualTime, 'HH:mm') : null}
                            onChange={(t) => t && patchStep(step.key, { actualTime: t.format('HH:mm') })}
                            disabled={disabled}
                            allowClear={false}
                          />
                        </div>
                      </div>
                    )}

                    <div className="sync-step-card-body">
                      <Text className="sync-step-action-line">{formatSyncStepActionLine(step)}</Text>
                      {step.productExamples && (
                        <Text type="secondary" className="sync-step-products">
                          Example products: {step.productExamples}
                        </Text>
                      )}
                      {!isAi && (
                        <div className="sync-step-field">
                          <Text type="secondary" className="sync-step-field-label">Inventory item</Text>
                          {step.inventory_deducted && !scheduleOnly ? (
                            <Tag color="purple">Stock deducted{step.inventory_item_name ? ` · ${step.inventory_item_name}` : ''}</Tag>
                          ) : (
                            <>
                              <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Bind stock — deducted when step is marked done"
                                value={step.inventory_item_id || undefined}
                                options={
                                  step.inventory_item_id && !inventoryOptions.some((opt) => opt.value === step.inventory_item_id)
                                    ? [{
                                      value: step.inventory_item_id,
                                      label: step.inventory_item_name || `Item #${step.inventory_item_id}`,
                                      name: step.inventory_item_name,
                                      stock: 0,
                                      unit: 'units',
                                      isLow: false,
                                    }, ...inventoryOptions]
                                    : inventoryOptions
                                }
                                disabled={disabled}
                                style={{ width: '100%' }}
                                onChange={(value, option) => patchStep(step.key, {
                                  inventory_item_id: value || null,
                                  inventory_item_name: option?.name || option?.label || null,
                                })}
                                optionRender={(option) => (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <span style={{ fontWeight: 500 }}>{option.data.name || option.label}</span>
                                    <Space size={4}>
                                      <Tag color={option.data.stock > 0 ? (option.data.isLow ? 'warning' : 'blue') : 'error'} style={{ margin: 0 }}>
                                        {option.data.stock != null ? `${option.data.stock.toLocaleString()} ${option.data.unit}` : ''}
                                      </Tag>
                                      {option.data.isLow && <Tag color="red" style={{ margin: 0 }}>Low Stock</Tag>}
                                    </Space>
                                  </div>
                                )}
                              />
                              {(() => {
                                const boundItem = inventoryItems.find((inv) => inv.id === step.inventory_item_id);
                                if (!boundItem) return null;
                                const stock = Number(boundItem.current_stock || 0);
                                const unit = boundItem.unit || 'units';
                                const isLow = Boolean(boundItem.is_low_stock || (boundItem.min_stock != null && stock <= Number(boundItem.min_stock)));
                                return (
                                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                                    Stock available: <strong style={{ color: stock > 0 ? '#1677ff' : '#ff4d4f' }}>{stock.toLocaleString()} {unit}</strong>
                                    {stock <= 0 && <Tag color="red" style={{ marginLeft: 6 }}>Out of stock</Tag>}
                                    {stock > 0 && isLow && <Tag color="warning" style={{ marginLeft: 6 }}>Low stock</Tag>}
                                  </Text>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      )}
                      {step.instructions && (
                        <Text type="secondary" className="sync-step-instruction">{step.instructions}</Text>
                      )}

                      {!isAi && step.action === 'inject' && (
                        <div className="sync-step-field">
                          <Text type="secondary" className="sync-step-field-label">Dosage (ml)</Text>
                          {step.dosage && (
                            <Text type="secondary" className="sync-step-dose-hint">
                              Recommended: {step.dosage}
                            </Text>
                          )}
                          <InputNumber
                            min={0}
                            step={0.1}
                            precision={2}
                            size={controlSize}
                            style={{ width: '100%' }}
                            placeholder="Enter ml"
                            value={parseDosageMl(step.actualDosage) ?? parseDosageMl(step.dosage)}
                            onChange={(value) => patchStep(step.key, {
                              actualDosage: value === null || value === undefined ? null : value,
                            })}
                            disabled={disabled}
                          />
                        </div>
                      )}

                      {!isAi && step.route && (
                        <div className="sync-step-dose-item sync-step-route-row">
                          <Text type="secondary" className="sync-step-dose-label">Route</Text>
                          <Text strong className="sync-step-dose-value">{step.route}</Text>
                        </div>
                      )}

                      {!isAi && step.action !== 'inject' && step.dosage && (
                        <div className="sync-step-dose-item sync-step-route-row">
                          <Text type="secondary" className="sync-step-dose-label">Dosage</Text>
                          <Text strong className="sync-step-dose-value">{step.dosage}</Text>
                        </div>
                      )}

                      <div className="sync-step-field">
                        <Text type="secondary" className="sync-step-field-label">
                          {isAi ? 'Insemination notes' : 'Field notes'}
                        </Text>
                        <TextArea
                          rows={isMobile ? 3 : 2}
                          size={controlSize}
                          placeholder={
                            isAi
                              ? 'Technician, semen straw ID, heat signs…'
                              : 'Optional notes for this step'
                          }
                          value={step.notes || ''}
                          onChange={(e) => patchStep(step.key, { notes: e.target.value })}
                          disabled={disabled}
                        />
                      </div>
                    </div>

                    {isDone ? (
                      <div className="sync-step-done-badge">
                        <CheckCircleOutlined /> Completed
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {totalSteps > 0 && (
            <Collapse
              className="sync-written-collapse"
              items={[{
                key: 'guide',
                label: 'Printable field instructions',
                children: <SyncProtocolWrittenGuide syncData={state} />,
              }]}
            />
          )}

          {displayPreset.sourceNote ? (
            <Text type="secondary" className="sync-protocol-source">{displayPreset.sourceNote}</Text>
          ) : null}

          <Alert
            type="warning"
            showIcon
            className="sync-protocol-disclaimer"
            style={
              {
                marginTop: 16,
              }
            }
            message="Always confirm doses, routes, and withdrawal periods with your veterinarian and product label."
          />
        </>
      )}

      {!state.presetId && speciesKey && presets.length === 0 && (
        <Alert
          type="warning"
          showIcon
          message={`No preset sync protocols for ${speciesLabel || 'this species'} yet.`}
          description="You can still record synchronized breeding with notes, or assign the animal to a supported species category."
        />
      )}
    </div>
  );
}

export default SyncProtocolScheduler;
